import { useAuthContext } from "@/context/auth-context";
import { useIsApiReady } from "@/hooks/use-api-ready";
import {
  organizationControllerCreate,
  organizationControllerInviteMember,
  organizationControllerList,
  organizationControllerLookupUser,
  organizationControllerListMembers,
  organizationControllerRemoveMember,
  organizationControllerRename,
  organizationControllerSetMemberRole,
} from "@/lib/gen-api";
import type {
  GetOrganizationDto,
  GetOrganizationMembersDto,
} from "@/lib/gen-api/types.gen";
import { queryKeys } from "@/lib/query-keys";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

const refreshOrganizationMembership = (params: {
  queryClient: QueryClient;
  organizationUuid: string;
  userUuid: string | undefined;
}) => {
  params.queryClient.invalidateQueries({
    queryKey: queryKeys.organizationMembers(params.organizationUuid),
  });
  params.queryClient.invalidateQueries({
    queryKey: queryKeys.userGroups(params.userUuid),
  });
  params.queryClient.invalidateQueries({
    queryKey: queryKeys.group(),
  });
};

export type OrganizationMember = GetOrganizationMembersDto["data"][number];

export const useOrganizationsData = () => {
  const queryClient = useQueryClient();
  const isApiReady = useIsApiReady();

  const { data, isPending, error } = useQuery({
    enabled: isApiReady,
    queryKey: queryKeys.organizations(),
    queryFn: () =>
      organizationControllerList().then((res) => res.data?.data ?? []),
  });

  const { mutateAsync: createOrganization, isPending: isCreating } =
    useMutation({
      mutationFn: async (params: { name: string; slug: string }) => {
        const response = await organizationControllerCreate({
          body: params,
        });
        if (response.response.status !== 201 || !response.data) {
          throw new Error("Failed to create organization");
        }
        return response.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations(),
        });
      },
    });

  const { mutateAsync: renameOrganization, isPending: isRenaming } =
    useMutation({
      mutationFn: async (params: {
        organizationUuid: string;
        name: string;
      }) => {
        const response = await organizationControllerRename({
          path: { organizationUuid: params.organizationUuid },
          body: { name: params.name },
        });
        if (response.response.status !== 200 || !response.data) {
          throw new Error("Failed to rename organization");
        }
        return response.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations(),
        });
      },
    });

  return {
    organizations: data ?? [],
    isLoading: isPending,
    error,
    createOrganization,
    isCreating,
    renameOrganization,
    isRenaming,
  };
};

export const useOrganizationData = (
  organizationUuid: string | null | undefined,
) => {
  const { organizations, isLoading, renameOrganization, isRenaming } =
    useOrganizationsData();
  const organization =
    organizations.find(
      (item: GetOrganizationDto) => item.uuid === organizationUuid,
    ) ?? null;

  return {
    organization,
    isLoading,
    renameOrganization,
    isRenaming,
  };
};

export const useOrganizationUserLookup = (
  organizationUuid: string | null | undefined,
) => {
  const { mutateAsync: lookupUser, isPending: isLookingUp } = useMutation({
    mutationFn: async (email: string) => {
      if (!organizationUuid) {
        throw new Error("Organization UUID is required");
      }
      const response = await organizationControllerLookupUser({
        path: { organizationUuid },
        query: { email: email.trim() },
      });
      if (response.response.status === 404) {
        throw new Error("No account exists for this email");
      }
      if (!response.response.ok || !response.data) {
        throw new Error("Failed to look up user");
      }
      return response.data;
    },
  });

  return { lookupUser, isLookingUp };
};

export const useInviteOrganizationMember = (
  organizationUuid: string | null | undefined,
) => {
  const queryClient = useQueryClient();
  const { authDetails } = useAuthContext();

  const { mutateAsync: inviteMember, isPending: isInviting } = useMutation({
    mutationFn: async (userUuid: string) => {
      if (!organizationUuid) {
        throw new Error("Organization UUID is required");
      }
      const response = await organizationControllerInviteMember({
        path: { organizationUuid },
        body: { userUuid },
      });
      if (!response.response.ok) {
        throw new Error("Failed to invite member");
      }
    },
    onSettled: () => {
      if (!organizationUuid) return;
      refreshOrganizationMembership({
        queryClient,
        organizationUuid,
        userUuid: authDetails?.uuid,
      });
    },
  });

  return { inviteMember, isInviting };
};

export const useOrganizationMembersData = (
  organizationUuid: string | null | undefined,
) => {
  const queryClient = useQueryClient();
  const { authDetails } = useAuthContext();
  const isApiReady = useIsApiReady();

  const { data, isPending, error } = useQuery({
    enabled: isApiReady && !!organizationUuid,
    queryKey: queryKeys.organizationMembers(organizationUuid ?? ""),
    queryFn: () =>
      organizationControllerListMembers({
        path: { organizationUuid: organizationUuid! },
      }).then((res) => res.data?.data ?? []),
  });

  const invalidateMembers = () => {
    if (!organizationUuid) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.organizationMembers(organizationUuid),
    });
  };

  const invalidateMembership = () => {
    if (!organizationUuid) return;
    refreshOrganizationMembership({
      queryClient,
      organizationUuid,
      userUuid: authDetails?.uuid,
    });
  };

  const { mutateAsync: inviteMember, isPending: isInviting } = useMutation({
    mutationFn: async (userUuid: string) => {
      if (!organizationUuid) {
        throw new Error("Organization UUID is required");
      }
      const response = await organizationControllerInviteMember({
        path: { organizationUuid },
        body: { userUuid },
      });
      if (!response.response.ok) {
        throw new Error("Failed to invite member");
      }
    },
    onSettled: invalidateMembership,
  });

  const { mutateAsync: updateMemberRole, isPending: isUpdatingRole } =
    useMutation({
      mutationFn: async (params: {
        userUuid: string;
        role: OrganizationMember["role"];
      }) => {
        if (!organizationUuid) {
          throw new Error("Organization UUID is required");
        }
        const response = await organizationControllerSetMemberRole({
          path: { organizationUuid, userUuid: params.userUuid },
          body: { role: params.role },
        });
        if (!response.response.ok) {
          throw new Error("Failed to update organization role");
        }
      },
      onSettled: invalidateMembers,
    });

  const { mutateAsync: removeMember, isPending: isRemoving } = useMutation({
    mutationFn: async (userUuid: string) => {
      if (!organizationUuid) {
        throw new Error("Organization UUID is required");
      }
      const response = await organizationControllerRemoveMember({
        path: { organizationUuid, userUuid },
      });
      if (!response.response.ok) {
        throw new Error("Failed to remove member");
      }
    },
    onSettled: invalidateMembership,
  });

  return {
    members: data ?? [],
    isLoading: isPending && !!organizationUuid,
    error,
    inviteMember,
    isInviting,
    updateMemberRole,
    isUpdatingRole,
    removeMember,
    isRemoving,
  };
};
